import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";

import {
  fetchEmployeeById,
  updateEmployee,
  resetStatus,
  fetchAllDesignations,
} from "../../store/slices/userSlice";

import { fetchAllDepartments } from "../../store/slices/departmentSlice";

import { fetchCountries } from "../../store/slices/locationSlice";

import { encryptBankingData } from "../../utils/cryptoUtils";

import StatusPopup from "../../components/popups/StatusPopup";

import EditSuccessPopup from "../../components/popups/EditSuccessPopup";

import EditPresnolDetail from "../../components/form/EditPresnolDetail";

import BankingDetail from "../../components/form/BankingDetail";

import EditJobDetails from "../../components/form/EditJobDetails";

import EditAction from "../../components/form/EditAction";

import EditPayTaxes from "../../components/form/EditPayTaxes";

const EditEmployee = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const location = useLocation();

  const fromPath = location.state?.from || "/dashboard";

  const id = location.state?.id;

  const EDIT_KEY = `editEmployee_${id}`;

  // EMPLOYEE DATA
  const selectedUser = useAppSelector(
    (s) => s.user.entityByKey[EDIT_KEY] || null,
  );

  const isInitialLoading = useAppSelector(
    (s) => s.user.entityLoadingByKey[EDIT_KEY] || false,
  );

  // UPDATE STATUS
  const {
    isSuccess,
    errorMessage,
    loading: isUpdating,
  } = useAppSelector((s) => s.user);

  // LOCATION
  const {
    countries = [],
    states = [],
    cities = [],
  } = useAppSelector((state) => state.location || {});

  const [employeeFormData, setEmployeeFormData] = useState({});

  const [isHashing, setIsHashing] = useState(false);

  // RESET STATUS
  useEffect(() => {
    dispatch(resetStatus());

    return () => dispatch(resetStatus());
  }, [dispatch]);

  // FETCH EMPLOYEE
  useEffect(() => {
    if (id) {
      dispatch(
        fetchEmployeeById({
          id,
          key: EDIT_KEY,
        }),
      );
    }
  }, [id, dispatch, EDIT_KEY]);

  // SET FORM DATA
  useEffect(() => {
    if (selectedUser) {
      setEmployeeFormData(selectedUser);
    }
  }, [selectedUser]);

  // FETCH INITIAL DATA
  useEffect(() => {
    dispatch(fetchAllDepartments());

    dispatch(fetchAllDesignations());

    dispatch(fetchCountries());
  }, [dispatch]);

  // POPUP CLOSE
  const handlePopupClose = () => {
    if (isSuccess) {
      navigate(fromPath, {
        replace: true,
      });
    }

    dispatch(resetStatus());
  };

  // FORM DATA CHANGE
  const handleDataChange = (updatedData) => {
    setEmployeeFormData((prev) => {
      return {
        ...prev,
        ...updatedData,
      };
    });
  };

  // UPDATE
  const handleUpdate = async () => {
    if (!id || !employeeFormData) return;

    setIsHashing(true);

    try {
      const formData = new FormData();

      formData.append("name", employeeFormData.name || "");

      formData.append("email", employeeFormData.email || "");

      formData.append("phone", employeeFormData.phone || "");

      formData.append(
        "addressLine1",
        employeeFormData.addressLine1 ||
          employeeFormData.addressLine ||
          employeeFormData.address ||
          "",
      );

      formData.append(
        "cityId",
        employeeFormData.cityId || employeeFormData.CityId || "",
      );

      formData.append("postalCode", employeeFormData.postalCode || "");

      formData.append("designationId", employeeFormData.designationId || "");

      formData.append("departmentId", employeeFormData.departmentId || "");

      formData.append(
        "payType",
        employeeFormData.payType === "Hourly"
          ? 0
          : employeeFormData.payType === "Salary"
            ? 1
            : "",
      );

      // PAY TYPE
      if (employeeFormData.payType === "Hourly") {
        formData.append("hourlyRate", employeeFormData.hourlyRate || "");

        formData.append(
          "defaultRegularHours",
          employeeFormData.defaultRegularHours || "",
        );

        formData.append("fixedSalary", "");
      } else if (employeeFormData.payType === "Salary") {
        formData.append("fixedSalary", employeeFormData.fixedSalary || "");

        formData.append("hourlyRate", "");

        formData.append("defaultRegularHours", "");
      }

      // BANK DETAILS
      const bankHolderName =
        employeeFormData.BankHolderName ||
        employeeFormData.bankHolderName ||
        "";

      const bankName =
        employeeFormData.BankName || employeeFormData.bankName || "";

      const accountNumber =
        employeeFormData.AccountNumber || employeeFormData.accountNumber || "";

      const routingNumber =
        employeeFormData.RoutingNumber || employeeFormData.routingNumber || "";

      const description =
        employeeFormData.Description || employeeFormData.description || "";

      const encryptedBankingData = await encryptBankingData({
        bankHolderName,
        bankName,
        accountNumber,
        routingNumber,
        description,
      });

      formData.append(
        "SaveBankDTO.BankHolderName",
        encryptedBankingData.EncryptedBankHolderName,
      );

      formData.append(
        "SaveBankDTO.BankName",
        encryptedBankingData.EncryptedBankName,
      );

      formData.append(
        "SaveBankDTO.AccountNumber",
        encryptedBankingData.EncryptedAccountNumber,
      );

      formData.append(
        "SaveBankDTO.RoutingNumber",
        encryptedBankingData.EncryptedRoutingNumber,
      );

      formData.append("SaveBankDTO.AccountType", "C");

      formData.append(
        "SaveBankDTO.Description",
        encryptedBankingData.EncryptedDescription,
      );

      formData.append("payrollCycle", employeeFormData.payrollCycle || "");

      // TAX IDS
      const finalTaxIds =
        employeeFormData.taxIds ||
        (Array.isArray(employeeFormData.formulas)
          ? employeeFormData.formulas.map((f) =>
              typeof f === "object" ? f.id : f,
            )
          : []);

      if (finalTaxIds.length) {
        finalTaxIds.forEach((taxId) => {
          formData.append("taxIds", taxId);
        });
      }

      // IMAGE
      if (employeeFormData.file) {
        formData.append("file", employeeFormData.file);
      }

      dispatch(
        updateEmployee({
          id: Number(id),
          data: formData,
        }),
      );
    } catch (error) {
      console.error("Error updating employee:", error);
    } finally {
      setIsHashing(false);
    }
  };

  // INITIAL LOADER
  if (isInitialLoading && !selectedUser) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{
          height: "80vh",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading</span>
        </div>

        <h5 className="ms-3 mb-0">Fetching Employee Data</h5>
      </div>
    );
  }

  return (
    <>
      <div
        className={
          isUpdating || isHashing ? "opacity-50 pointer-events-none" : ""
        }
      >
        <EditAction
          clientData={employeeFormData}
          onUpdate={handleUpdate}
          isLoading={isUpdating || isHashing}
        />

        <div>
          {/* PERSONAL DETAIL */}
          <EditPresnolDetail
            clientData={employeeFormData}
            onDataChange={handleDataChange}
            // LOCATION
            countries={countries}
            states={states}
            cities={cities}
          />

          {/* JOB DETAIL */}
          <EditJobDetails
            clientData={employeeFormData}
            onDataChange={handleDataChange}
          />

          {/* PAY TAX */}
          <EditPayTaxes
            clientData={employeeFormData}
            onDataChange={handleDataChange}
            showTaxReminder={false}
          />

          {/* BANK DETAIL */}
          <BankingDetail
            clientData={employeeFormData}
            onDataChange={handleDataChange}
          />
        </div>
      </div>

      {/* SUCCESS */}
      <EditSuccessPopup
        show={isSuccess === true}
        onHide={handlePopupClose}
        entityType="employee"
        operationType="update"
        title="Success"
      />

      {/* ERROR */}
      <StatusPopup
        show={!!errorMessage}
        type="error"
        title="Error"
        message={
          Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage
        }
        handleClose={handlePopupClose}
      />
    </>
  );
};

export default EditEmployee;
